import { useState } from 'react';

import { selectCurrentUser } from 'features/auth/slice';
import { useSelector } from 'react-redux';

import useComments from 'lib/hooks/useComments';

import { flash, recordsMatch } from '@frontendmentorio/design-system-v2';

import Content from './Content';
import CommentForm from './Form';
import MarkHelpful from '../MarkHelpful';
import ReplyButton from './ReplyButton';
import UpvoteButton from '../UpvoteButton';

export default function CommentCard({ comment, solution }) {
  const { create } = useComments();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const currentUser = useSelector(selectCurrentUser);
  const isAuthor = recordsMatch(comment.user, currentUser);
  const isOwner = recordsMatch(solution?.user, currentUser);
  const isUpvoteDisabled = !comment.content || isAuthor || !comment.user;
  const canMarkHelpful =
    comment.content && comment.user && !isAuthor && isOwner;

  const handleSubmit = async (data, state, methods) => {
    setIsLoading(true);

    try {
      const reply = await create({
        ...data,
        parent: comment.id,
        solution: solution.id,
      });

      methods.reset();
      window.location.replace(`#comment-${reply.id}`);
    } catch (error) {
      flash.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!comment) return null;

  return (
    <Content comment={comment}>
      <div className="flex flex-wrap-reverse gap-3">
        <UpvoteButton comment={comment} disabled={isUpvoteDisabled} />
        <ReplyButton
          comment={comment}
          disabled={!comment.replies?.length && !currentUser}
          isOpen={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        />

        <MarkHelpful
          className="ml-auto"
          comment={comment}
          isAwardable={canMarkHelpful}
        />
      </div>

      {isOpen && (
        <div className="border-l border-neutral-200 pl-4 desktop:ml-4">
          {comment.replies?.length > 0 && (
            <ul className="flex flex-col mb-4">
              {comment.replies.map((reply) => (
                <li
                  className={`border-neutral-200${
                    currentUser ? ' border-b py-4' : ''
                  }`}
                  key={reply.id}
                >
                  <Content comment={reply} />
                </li>
              ))}
            </ul>
          )}

          {currentUser && (
            <CommentForm
              ariaLabel={`Reply to ${comment.user.displayName}'s comment`}
              buttonText={isLoading ? 'Submitting...' : 'Post reply'}
              disabled={isLoading}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      )}
    </Content>
  );
}
